import type { ToolDefinition } from '../types';
import { PosePanel } from './panel';
import { DEFAULT_POSE_PARAMS, normalizePoseParams, type PoseParams } from './types';
import { buildPoseImageNodeDataDataUrl, buildPoseImageNodeDataFromUrl, uploadPosePngAndGetUrl } from './config';
import { imageModels } from '@flowmuse/models/image';

export const poseTool: ToolDefinition<PoseParams> = {
  id: 'pose',
  label: 'tool.pose.name',
  defaultParams: DEFAULT_POSE_PARAMS,
  normalize: normalizePoseParams,
  Panel: ({ params, onChange }) => <PosePanel params={params} onChange={onChange} />,
  onRun: async ({ params, now, auth }) => {
    // 1) Generate a pose guideline image (prefer uploaded URL; fallback to PNG/SVG data URL)
    let url: string | undefined;
    let type = 'image/png';
    if (auth?.token && auth?.userId) {
      try {
        const uploaded = await uploadPosePngAndGetUrl({ params, auth });
        url = uploaded.url;
        type = uploaded.type || 'image/png';
      } catch {
        // ignore and fall back
      }
    }
    if (!url) {
      const poseNodeData = await buildPoseImageNodeDataDataUrl({ params, now });
      const contentUrl = (poseNodeData as any)?.content?.url as string | undefined;
      const contentType = (poseNodeData as any)?.content?.type as string | undefined;
      if (contentUrl) url = contentUrl;
      if (contentType) type = contentType;
    }
    if (!url) {
      // should never happen, but keep runtime safe
      return { connectFromSource: false, autoRun: false };
    }

    const PROMPT =
      'A full-body image of a person in a dynamic pose, based on a simplified guideline reference. The character should have realistic proportions, natural movement, and detailed anatomy. Exclude any stick figures, lines, or schematic references in the final image.';
    const MODEL_ID = 'nano-banana-2-2k';

    // 2) Create: (upload node) + (pose preview node) -> (image transform node)
    return {
      createGraph: {
        nodes: [
          {
            key: 'upload',
            type: 'upload',
            data: {
              // User uploads their own reference image here
              fileType: 'image',
              updatedAt: now,
            },
          },
          {
            key: 'pose',
            type: 'image',
            data: {
              ...(buildPoseImageNodeDataFromUrl({ url, now }) as any),
              name: 'Pose',
            },
          },
          {
            key: 'image',
            type: 'image',
            data: {
              viewMode: 'transform',
              model: MODEL_ID,
              instructions: PROMPT,
              updatedAt: now,
              generated: undefined,
              status: undefined,
              processingTaskId: undefined,
              processingGenerationId: undefined,
            } as any,
          },
        ],
        edges: [
          { sourceKey: 'upload', targetKey: 'image', type: 'animated' },
          { sourceKey: 'pose', targetKey: 'image', type: 'animated' },
        ],
        selectKey: 'image',
      },
      connectFromSource: false,
      autoRun: false,
    };
  },
};

export type { PoseParams };
export { DEFAULT_POSE_PARAMS, normalizePoseParams };


